import { FetchMoreOptions, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import FiltersSortHeaderStaticColumn from "components/Filters/SortHeader/StaticColumn";
import FiltersSortHeaderWrapper from "components/Filters/SortHeader/Wrapper";
import FlashContext from "components/FlashMessages/FlashContext";
import { EmptystateRunsColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PageWrapper from "components/PageWrapper";
import InfiniteScroll from "components/scroll/InfiniteScroll";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import Callout from "ds/components/Callout";
import EmptyState from "ds/components/EmptyState";
import FeedbackActions from "ds/components/Feedback/Actions";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Typography from "ds/components/Typography";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { RunIgnoredTrigger } from "types/generated";
import { uniqBy } from "utils/uniq";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl, groupPerCommitOnTimeline } from "../helpers";
import { COLUMN_GAP, COLUMN_ORDER } from "./constants";
import StackIgnoredRunsElement from "./Element";
import { FetchListIgnoredRunsData, LIST_IGNORED_RUNS } from "./gql";
import StackIgnoredRunsGroup from "./Group";

const StackIgnoredRunsFeatureAvailable = () => {
  const { onError } = useTypedContext(FlashContext);
  const { stack: stackCtx } = useTypedContext(StackContext);

  useTitle(`Ignored runs · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { loading, error, data, fetchMore } = useQuery(LIST_IGNORED_RUNS, {
    variables: { stackId: stackCtx.id },
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
  });

  const updateQuery = useCallback<
    Exclude<FetchMoreOptions<FetchListIgnoredRunsData>["updateQuery"], undefined>
  >((previous, { fetchMoreResult }) => {
    if (
      fetchMoreResult &&
      fetchMoreResult.stack?.searchRunIgnoredTriggers &&
      (fetchMoreResult.stack.searchRunIgnoredTriggers.edges?.length || 0) > 0
    ) {
      return {
        stack: {
          ...previous.stack,
          ...fetchMoreResult.stack,
          searchRunIgnoredTriggers: {
            pageInfo: fetchMoreResult.stack.searchRunIgnoredTriggers.pageInfo,
            edges: uniqBy(
              [
                ...(previous.stack?.searchRunIgnoredTriggers?.edges || []),
                ...(fetchMoreResult.stack.searchRunIgnoredTriggers?.edges || []),
              ],
              (edge) => edge.node.id
            ),
          },
        },
      };
    }

    return previous;
  }, []);

  const stack = useMemo(() => {
    if (data?.stack) {
      return {
        ...stackCtx,
        ...(data?.stack ? data.stack : {}),
      };
    }

    return undefined;
  }, [data, stackCtx]);

  const runsListPerCommit = useMemo(
    () =>
      groupPerCommitOnTimeline<RunIgnoredTrigger>(
        stack?.searchRunIgnoredTriggers?.edges.map((edge) => edge.node) || []
      ),
    [stack?.searchRunIgnoredTriggers]
  );

  const handleScrollEnd = useCallback(async () => {
    const lastRun = stack?.searchRunIgnoredTriggers?.pageInfo.endCursor;

    if (lastRun) {
      try {
        await fetchMore({
          variables: { after: lastRun },
          updateQuery,
        });
      } catch (err) {
        onError(err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMore, stack]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  // Do not show 'loading' when polling in the background.
  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack || !stack) {
    return <NotFoundPage />;
  }

  return (
    <>
      <StackHeader />

      <PageInfoHeader title="Ignored runs" />

      <Callout variant="warning">
        If you want to force the run, sync and trigger it manually.
        <FeedbackActions>
          <LinkButton
            variant="secondary"
            size="small"
            to={`/stack/${stack.id}`}
            analyticsTitle="Go to tracked runs clicked"
          >
            Go to tracked runs
          </LinkButton>
        </FeedbackActions>
      </Callout>

      <PageWrapper>
        <InfiniteScroll
          onScrollEnd={handleScrollEnd}
          hasMore={data.stack.searchRunIgnoredTriggers?.pageInfo.hasNextPage}
          loading={loading}
        >
          {runsListPerCommit.length > 0 && (
            <Box gap="large" direction="column">
              {runsListPerCommit.map(([hash, group], i) => {
                return (
                  <StackIgnoredRunsGroup
                    key={`${hash}_${i}`}
                    run={group[0]}
                    provider={stack.provider}
                    count={group.length}
                    isOpen
                  >
                    <FiltersSortHeaderWrapper columnOrder={COLUMN_ORDER} columnGap={COLUMN_GAP}>
                      <FiltersSortHeaderStaticColumn>
                        <Typography tag="span" variant="p-t7">
                          Status
                        </Typography>
                      </FiltersSortHeaderStaticColumn>
                      <FiltersSortHeaderStaticColumn>
                        <Typography tag="span" variant="p-t7">
                          Data
                        </Typography>
                      </FiltersSortHeaderStaticColumn>
                      <FiltersSortHeaderStaticColumn>
                        <Typography tag="span" variant="p-t7">
                          Drift detected
                        </Typography>
                      </FiltersSortHeaderStaticColumn>
                    </FiltersSortHeaderWrapper>
                    {group.map((run) => (
                      <StackIgnoredRunsElement key={run.id} run={run} />
                    ))}
                  </StackIgnoredRunsGroup>
                );
              })}
            </Box>
          )}

          {(stack.searchRunIgnoredTriggers?.edges.length || 0) === 0 && (
            <EmptyState
              icon={EmptystateRunsColored}
              title="No ignored runs yet"
              caption={<>No runs were ignored for last 14 days</>}
            />
          )}
        </InfiniteScroll>
      </PageWrapper>
    </>
  );
};

export default StackIgnoredRunsFeatureAvailable;
